/**
 * Styling utilities for tasks
 * Provides consistent color coding for priority, status, and due dates
 */

import { isAfter, isBefore, startOfDay, addDays } from 'date-fns';
import type { Timestamp } from 'firebase/firestore';

/**
 * Get priority badge styling
 * P1 = Red (urgent), P2 = Orange (high), P3 = Yellow (medium), P4-5 = Gray (low)
 */
export function getPriorityStyles(priority?: number) {
  if (!priority) return null;

  switch (priority) {
    case 1:
      return {
        className: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
        label: 'P1',
        icon: '🔴',
      };
    case 2:
      return {
        className: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20',
        label: 'P2',
        icon: '🟠',
      };
    case 3:
      return {
        className: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20',
        label: 'P3',
        icon: '🟡',
      };
    default:
      return {
        className: 'bg-muted/50 text-muted-foreground border-border',
        label: `P${priority}`,
        icon: '⚪',
      };
  }
}

/**
 * Get due date styling based on urgency
 * Overdue = Red, Due today = Amber, Due soon = Blue, Future = Gray
 */
export function getDueDateStyles(dueAt?: Timestamp | Date | null) {
  if (!dueAt) return null;

  const dueDate = dueAt instanceof Date ? dueAt : dueAt.toDate();
  const now = new Date();
  const today = startOfDay(now);
  const tomorrow = addDays(today, 1);
  const nextWeek = addDays(today, 7);

  // Overdue (past due)
  if (isBefore(dueDate, today)) {
    return {
      className: 'text-red-600 dark:text-red-400 font-medium',
      icon: '🔴',
      label: 'Overdue',
    };
  }

  // Due today
  if (isBefore(dueDate, tomorrow)) {
    return {
      className: 'text-amber-600 dark:text-amber-400 font-medium',
      icon: '🟡',
      label: 'Due today',
    };
  }

  // Due this week
  if (isBefore(dueDate, nextWeek)) {
    return {
      className: 'text-blue-600 dark:text-blue-400',
      icon: '🔵',
      label: 'Due soon',
    };
  }

  // Future
  return {
    className: 'text-muted-foreground',
    icon: '⚪',
    label: 'Upcoming',
  };
}

/**
 * Get card border styling based on priority and due date
 * High priority or overdue tasks get colored borders
 */
export function getCardBorderStyles(priority?: number, dueAt?: Timestamp | Date | null) {
  // Check if overdue
  if (dueAt) {
    const dueDate = dueAt instanceof Date ? dueAt : dueAt.toDate();
    const today = startOfDay(new Date());
    
    if (isBefore(dueDate, today)) {
      return 'border-red-500/30 bg-red-500/5';
    }
  }

  // Check priority
  if (priority === 1) {
    return 'border-red-500/20 bg-red-500/5';
  }

  if (priority === 2) {
    return 'border-orange-500/20';
  }

  return 'border-border';
}

/**
 * Check if task is overdue
 */
export function isTaskOverdue(dueAt?: Timestamp | Date | null): boolean {
  if (!dueAt) return false;
  const dueDate = dueAt instanceof Date ? dueAt : dueAt.toDate();
  return isBefore(dueDate, startOfDay(new Date()));
}

/**
 * Check if task is due today
 */
export function isTaskDueToday(dueAt?: Timestamp | Date | null): boolean {
  if (!dueAt) return false;
  const dueDate = dueAt instanceof Date ? dueAt : dueAt.toDate();
  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  return isAfter(dueDate, today) && isBefore(dueDate, tomorrow);
}
